/**
 * Message types that can be posted to the parent frame
 */
export enum EmbeddedStorefrontMessageType {
  /** Posted when a purchase is successfully completed */
  PURCHASE_COMPLETE = 'embedded-storefront-purchase-complete',
  /** Posted when the user requests to exit the storefront */
  EXIT_REQUESTED = 'embedded-storefront-exit-requested',
}

/**
 * Base message structure for all parent frame communications
 */
type BaseMessage = {
  type: EmbeddedStorefrontMessageType;
};

/**
 * Message posted when a purchase is completed
 */
export type PurchaseCompleteMessage = BaseMessage & {
  type: EmbeddedStorefrontMessageType.PURCHASE_COMPLETE;
  orderId?: string;
  timestamp: string;
};

/**
 * Message posted when user requests to exit
 */
export type ExitRequestedMessage = BaseMessage & {
  type: EmbeddedStorefrontMessageType.EXIT_REQUESTED;
  timestamp: string;
};

/**
 * Message types for embedded Ship Laptop page
 */
export enum EmbeddedShipLaptopMessageType {
  /** Posted when a shipment is successfully created */
  SHIPMENT_COMPLETE = 'embedded-ship-laptop-shipment-complete',
  /** Posted when a shipment creation fails */
  SHIPMENT_ERROR = 'embedded-ship-laptop-shipment-error',
  /** Posted when the user requests to exit the ship laptop form */
  EXIT_REQUESTED = 'embedded-ship-laptop-exit-requested',
}

/**
 * Union type of all possible storefront messages
 */
export type EmbeddedStorefrontMessage =
  | PurchaseCompleteMessage
  | ExitRequestedMessage;

/**
 * Message posted when a shipment is successfully created
 */
export type ShipmentCompleteMessage = {
  type: EmbeddedShipLaptopMessageType.SHIPMENT_COMPLETE;
  shipmentId?: string;
  timestamp: string;
};

/**
 * Message posted when a shipment creation fails
 */
export type ShipmentErrorMessage = {
  type: EmbeddedShipLaptopMessageType.SHIPMENT_ERROR;
  error: string;
  timestamp: string;
};

/**
 * Message posted when user requests to exit ship laptop form
 */
export type ShipLaptopExitRequestedMessage = {
  type: EmbeddedShipLaptopMessageType.EXIT_REQUESTED;
  timestamp: string;
};

/**
 * Union type of all possible ship laptop messages
 */
export type EmbeddedShipLaptopMessage =
  | ShipmentCompleteMessage
  | ShipmentErrorMessage
  | ShipLaptopExitRequestedMessage;

/**
 * Message types for embedded First Login / signup flow
 */
export enum EmbeddedFirstLoginMessageType {
  /** Posted when signup completes successfully */
  SIGNUP_COMPLETE = 'embedded-first-login-signup-complete',
}

/**
 * Message posted when signup completes successfully
 */
export type SignupCompleteMessage = {
  type: EmbeddedFirstLoginMessageType.SIGNUP_COMPLETE;
  organization_id?: string;
  external_organization_id?: string;
  timestamp: string;
};

/**
 * Union type of all embedded first login messages
 */
export type EmbeddedFirstLoginMessage = SignupCompleteMessage;

/**
 * Union type of all embedded messages
 */
export type EmbeddedMessage =
  | EmbeddedStorefrontMessage
  | EmbeddedShipLaptopMessage
  | EmbeddedFirstLoginMessage;
